import { useTranslation } from 'react-i18next';

import { PlanReport } from '@cfa/pathway-fe-shared/xp';

import { mapCategoryToAliasTranslation } from '@/utils/categoryUtils';
import { itemByLanguage } from '@/utils/language';

import PlanCardCategoryIcon from '@/components/Cards/PlanCard/PlanCardContent/PlanCardCategoryIcon';
import { PathwayCard } from '@/components/pathway-card';
import { printPlan } from '@/components/print-report';
import { PrintCertificates } from '@/components/PrintCertificates';
import { PrintReportButton } from '@/components/PrintReportButton';

import { routes } from 'constants/index';

interface ReportsPlanCardProps {
  assignedUsers: number;
  completedUsers: number;
  plan: PlanReport;
  totalSteps?: number;
}

const ReportsPlanCard: React.FC<ReportsPlanCardProps> = ({
  assignedUsers,
  completedUsers,
  plan,
  totalSteps,
}) => {
  const { t } = useTranslation();

  const percentComplete = (completedUsers / assignedUsers) * 100;

  const renderPlanStatus = () => {
    if (!!assignedUsers && completedUsers === assignedUsers) {
      return (
        <PathwayCard.Subtitle tone="success" data-testid="PlanTaskCount">
          {t('Generic.completedByAll')}
        </PathwayCard.Subtitle>
      );
    }

    const teamMemberStatus = assignedUsers
      ? `${completedUsers}/${assignedUsers} ${t(
          'Generic.teamMembersCompleted',
        )}`
      : t('Generic.noTeamMembersAssigned');

    return (
      <PathwayCard.Subtitle data-testid="PlanTaskCount">
        {totalSteps === 0 ? t('Generic.noContentInPlan') : teamMemberStatus}
      </PathwayCard.Subtitle>
    );
  };

  const showProgress =
    !!assignedUsers && totalSteps !== 0 && percentComplete !== 100;

  return (
    <PathwayCard.Root
      className="animate-[fadeIn_linear_0.3s]"
      data-testid="ReportsPlanCard"
      link={routes.reports.planProgress({ planId: plan.id })}
    >
      <PathwayCard.Media>
        <PlanCardCategoryIcon category={plan.category} />
      </PathwayCard.Media>
      <PathwayCard.Body>
        <PathwayCard.Label>
          {t(mapCategoryToAliasTranslation(plan.category))}
        </PathwayCard.Label>
        <PathwayCard.Title>{itemByLanguage(plan.name)}</PathwayCard.Title>
        {renderPlanStatus()}
      </PathwayCard.Body>
      {!plan.readonly && !!plan.created && (
        <PathwayCard.Meta>
          <p className="text-variant-body2 hidden sm:block">
            {new Date(plan.created).toLocaleDateString('en-US')}
          </p>
        </PathwayCard.Meta>
      )}
      {showProgress && (
        <PathwayCard.Progress
          aria-label="Plan progress"
          value={percentComplete}
        />
      )}
      <PathwayCard.Actions>
        <div className="flex items-center gap-4">
          <PrintCertificates planId={plan.id} />
          <PrintReportButton
            onClick={e => {
              e.preventDefault();
              printPlan(plan);
            }}
          />
        </div>
      </PathwayCard.Actions>
    </PathwayCard.Root>
  );
};

export default ReportsPlanCard;
